import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useClickOutside } from "../../../hooks/useClickOutside";
import CloseIconNew from "../../shared/icons/CloseIconNew";
import CustomInput from "../../custom/custom-input/CustomInput";
import CustomButton from "../../custom/custom-button/CustomButton";
import style from "../common.module.scss";
import SwitchTabs from "../../shared/SwitchTabs/SwitchTabs";
import ClipIcon from "../../shared/icons/ClipIcon";
import MailIcon from "../../shared/icons/MailIcon";
import CustomSelect from "../../custom/custom-select";
import { Calendar, Check, Copy } from "lucide-react";
import { Expeditor, User } from "../../../services";

const InvitePartnerModal = ({ setShowModal, user }) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);

  const [activeTab, setActiveTab] = useState("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingTab, setIsChangingTab] = useState(false);
  const [contentDirection, setContentDirection] = useState("left");
  const [linkExpiry, setLinkExpiry] = useState({
    id: "3",
    name: t("modalAddTransportCompany.link.expiry.3days"),
  });
  const [generatedLink, setGeneratedLink] = useState("");
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useClickOutside(modalRef, () => {
    setShowModal(false);
  });

  const toggleOptions = [
    {
      key: "email",
      text: t("modalAddTransportCompany.tabs.email"),
      icon: <MailIcon />,
    },
    {
      key: "link",
      text: t("modalAddTransportCompany.tabs.link"),
      icon: <ClipIcon />,
    },
  ];

  const linkExpiryOptions = [
    { id: "3", name: t("modalAddTransportCompany.link.expiry.3days") },
    { id: "7", name: t("modalAddTransportCompany.link.expiry.7days") },
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;

    const currentIndex = toggleOptions.findIndex((option) => option.key === activeTab);
    const newIndex = toggleOptions.findIndex((option) => option.key === newTab);

    if (newIndex > currentIndex) {
      setContentDirection("right");
    } else {
      setContentDirection("left");
    }

    setIsChangingTab(true);

    setTimeout(() => {
      setActiveTab(newTab);
      setIsChangingTab(false);
    }, 150);
  };

  const handleSendInvitation = async () => {
    if (user && !user.permissions?.includes("partner_add")) {
      toast.info(t("common.noAccessSupportMessage"), {
        autoClose: 10000,
      });
      return;
    }

    if (!email) {
      setEmailError(t("modalAddTransportCompany.email.validation.required"));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t("modalAddTransportCompany.email.validation.invalid"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await Expeditor.createPartner("by-email", {
        email: email.trim(),
      });
      toast.success(t("modalAddTransportCompany.toast.inviteSuccess"));
      setShowModal(false);
    } catch (error) {
      console.error("Error sending invitation:", error);

      if (error.response?.status === 400) {
        setEmailError(t("modalAddTransportCompany.email.validation.invalid"));
      } else if (error.response?.status === 409) {
        setEmailError(t("modalAddTransportCompany.email.validation.exists"));
      } else {
        toast.error(t("modalAddTransportCompany.email.toast.error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    if (user && !user.permissions?.includes("partner_add")) {
      toast.info(t("common.noAccessSupportMessage"), {
        autoClose: 10000,
      });
      return;
    }

    setIsGeneratingLink(true);

    try {
      const response = await Expeditor.createPartner("by-link", {
        days: parseInt(linkExpiry.id),
      });
      const url = new URL(response.data.url);
      if (user?.type) {
        const receiverType = user.type === "Expeditor" ? "Client" : "Expeditor";
        url.searchParams.set("type", receiverType);
      }

      setGeneratedLink(url.toString());
    } catch (error) {
      console.error("Error generating link:", error);
      toast.error(t("common.genericError"));
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink).then(() => {
      setCopiedLink(true);
      toast.success(t("driversCreatePage.copied"));
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  return (
    <div className={style.modalOverlay}>
      <div ref={modalRef} className={style.modalContent}>
        <div className={style.modalHeader}>
          <h4>{t("modalAddTransportCompany.title")}</h4>
          <CloseIconNew onClick={() => setShowModal(false)} style={{ cursor: "pointer" }} />
        </div>

        <SwitchTabs options={toggleOptions} activeOption={activeTab} onOptionChange={handleTabChange} />

        <div
          className={`${style.inviteContent} ${isChangingTab ? style.fadeOut : style.fadeIn} ${
            style[`from-${contentDirection}`]
          }`}
        >
          {activeTab === "email" && (
            <>
              <div className={style.inviteTitle}>
                <h5>{t("modalAddTransportCompany.email.title")}</h5>
                <p>{t("modalAddTransportCompany.email.description")}</p>
              </div>

              <CustomInput
                placeholder={t("modalAddTransportCompany.email.placeholder")}
                labelText={t("modalAddTransportCompany.email.label")}
                errorText={emailError}
                inputType="email"
                value={email}
                onChangeValue={handleEmailChange}
              />

              <CustomButton
                text={t("modalAddTransportCompany.email.button")}
                onClick={handleSendInvitation}
                isFall
                isLoading={isLoading}
                isDark
                disabled={!!emailError}
              />
            </>
          )}

          {activeTab === "link" && (
            <>
              <div className={style.inviteTitle}>
                <h5>{t("modalAddTransportCompany.link.title")}</h5>
                <p>{t("modalAddTransportCompany.link.description")}</p>
              </div>

              <CustomSelect
                labelText={t("modalAddTransportCompany.link.expiryLabel")}
                placeholder={t("modalAddTransportCompany.link.expiryPlaceholder")}
                options={linkExpiryOptions}
                defaultValue={linkExpiry}
                returnObject
                bgWhite
                onSubmit={(item) => {
                  setLinkExpiry(item);
                }}
              />

              <CustomButton
                text={t("modalAddTransportCompany.link.button")}
                prefixStart={<Calendar color="#FFF" size={20} />}
                onClick={handleGenerateLink}
                isFall
                isLoading={isGeneratingLink}
                isDark
              />

              {generatedLink && (
                <div className={style.linkResult}>
                  <div className={style.linkContainer}>
                    <div className={style.linkInputWrapper}>
                      <input type="text" value={generatedLink} readOnly className={style.linkInput} />
                      <div className={style.copyButton} onClick={copyToClipboard}>
                        {copiedLink ? <Check color="#0A0A0A" /> : <Copy color="#0A0A0A" size={20} />}
                      </div>
                    </div>
                  </div>
                  <p className={style.linkInfo}>
                    {t("modalAddTransportCompany.link.validInfo", {
                      expiry: linkExpiry.name,
                    })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePartnerModal;
